import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/pages/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Network, Search, Activity, Code, MapPin, Globe, Copy, Refresh, CheckCircle, WarningCircle, OpenNewWindow, Trash,
} from "iconoir-react";
import { integrations, errDetail } from "@/lib/integrations";

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const Label = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{children}</div>
);

const StatusPill = ({ ready, labelReady = "Ready", labelOff = "Not configured" }) => (
  <Badge
    variant="outline"
    className={`font-mono text-[10px] uppercase tracking-widest ${
      ready ? "border-emerald-500 text-emerald-600" : "border-border text-muted-foreground"
    }`}
  >
    {ready ? labelReady : labelOff}
  </Badge>
);

function Section({ icon: Icon, title, desc, ready, children }) {
  return (
    <div className="border border-border bg-card" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 border border-border flex items-center justify-center">
            <Icon width={18} height={18} strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-app font-medium">{title}</div>
            <div className="text-xs text-muted-foreground mt-0.5 max-w-md">{desc}</div>
          </div>
        </div>
        {ready !== undefined && <StatusPill ready={ready} />}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function KeyField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        data-testid={testid}
        className="font-mono text-sm"
      />
    </div>
  );
}

function DataTable({ columns, rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-muted-foreground font-mono">No rows returned.</div>;
  }
  return (
    <div className="overflow-x-auto border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            {columns.map((c) => (
              <th key={c.key} className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/60 last:border-0">
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 truncate max-w-[260px]">{r[c.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  // local editable key state
  const [form, setForm] = useState({
    google_client_id: "",
    google_client_secret: "",
    google_pagespeed_api_key: "",
    bing_api_key: "",
    bing_site_url: "",
    gsc_site_url: "",
    ga4_property_id: "",
    gbp_account_id: "",
  });
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const s = await integrations.status();
      setStatus(s);
      setForm((f) => ({
        ...f,
        google_client_id: s.fields.google_client_id.preview || "",
        bing_site_url: s.fields.bing_site_url.preview || "",
        gsc_site_url: s.fields.gsc_site_url.preview || "",
        ga4_property_id: s.fields.ga4_property_id.preview || "",
        gbp_account_id: s.fields.gbp_account_id.preview || "",
      }));
    } catch (e) {
      toast.error(errDetail(e, "Failed to load integrations"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const g = params.get("google");
    if (g === "connected") { toast.success("Google account connected"); params.delete("google"); setParams(params, { replace: true }); }
    else if (g === "error") { toast.error("Google connection failed. Check Client ID/Secret & redirect URI."); params.delete("google"); setParams(params, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveKeys = async (keys) => {
    const payload = {};
    keys.forEach((k) => { if (form[k] !== undefined && !String(form[k]).includes("\u2022")) payload[k] = form[k]; });
    try {
      const s = await integrations.saveKeys(payload);
      setStatus(s);
      toast.success("Saved");
    } catch (e) {
      toast.error(errDetail(e, "Save failed"));
    }
  };

  const connectGoogle = async () => {
    try {
      // persist client id/secret first if entered
      await saveKeys(["google_client_id", "google_client_secret"]);
      const { auth_url } = await integrations.googleAuthUrl();
      window.location.href = auth_url;
    } catch (e) {
      toast.error(errDetail(e, "Could not start Google OAuth"));
    }
  };

  const disconnectGoogle = async () => {
    try { await integrations.googleDisconnect(); await load(); toast.success("Disconnected"); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const copyRedirect = () => {
    navigator.clipboard.writeText(status?.redirect_uri || "");
    toast.success("Redirect URI copied");
  };

  if (loading) {
    return (
      <AppShell title="Integrations" subtitle="Admin">
        <div className="font-mono text-sm uppercase tracking-widest text-muted-foreground">Loading\u2026</div>
      </AppShell>
    );
  }

  const sys = status?.systems || {};
  const google = status?.google || {};

  return (
    <AppShell title="Integrations" subtitle="Admin \u00b7 Owner only">
      <div className="max-w-4xl space-y-6" data-testid="integrations-page">
        <div className="border border-border bg-card p-5">
          <div className="flex items-center gap-3">
            <Network width={20} height={20} />
            <div>
              <div className="font-app font-medium">Data sources</div>
              <div className="text-xs text-muted-foreground">Connect external APIs once. Credentials are stored globally (server-side) and used across the platform.</div>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label>OAuth redirect URI (add this in Google Cloud \u2192 Credentials)</Label>
              <div className="font-mono text-xs break-all border border-border bg-muted/40 px-3 py-2" data-testid="redirect-uri">{status?.redirect_uri}</div>
            </div>
            <Button variant="outline" size="sm" onClick={copyRedirect} data-testid="copy-redirect"><Copy width={14} height={14} className="mr-1" /> Copy</Button>
          </div>
        </div>

        {/* GOOGLE OAUTH */}
        <Section icon={Globe} title="Google Connection (OAuth)" desc="One Google login powers Search Console, Analytics (GA4) and Business Profile. Enable those APIs in your Google Cloud project first." ready={google.connected}>
          <div className="grid sm:grid-cols-2 gap-4">
            <KeyField label="OAuth Client ID" value={form.google_client_id} onChange={setField("google_client_id")} placeholder="xxxx.apps.googleusercontent.com" testid="input-google-client-id" />
            <KeyField label="OAuth Client Secret" value={form.google_client_secret} onChange={setField("google_client_secret")} placeholder={status?.fields?.google_client_secret?.configured ? "\u2022\u2022\u2022\u2022 saved \u2014 re-enter to change" : "GOCSPX-..."} type="password" testid="input-google-client-secret" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => saveKeys(["google_client_id", "google_client_secret"])} data-testid="save-google-client">Save client</Button>
            {google.connected ? (
              <>
                <span className="flex items-center gap-2 text-sm text-emerald-600"><CheckCircle width={16} height={16} /> Connected{google.account_email ? ` as ${google.account_email}` : ""}</span>
                <Button variant="outline" size="sm" onClick={disconnectGoogle} data-testid="disconnect-google"><Trash width={14} height={14} className="mr-1" /> Disconnect</Button>
              </>
            ) : (
              <Button size="sm" onClick={connectGoogle} disabled={!google.oauth_client_ready && !(form.google_client_id && form.google_client_secret)} data-testid="connect-google">
                <OpenNewWindow width={14} height={14} className="mr-1" /> Connect Google
              </Button>
            )}
          </div>
          {!google.connected && <div className="text-xs text-muted-foreground flex items-center gap-1"><WarningCircle width={13} height={13} /> Save the client ID & secret, then click Connect to grant access.</div>}
        </Section>

        <SearchConsoleCard ready={sys.search_console?.ready} form={form} setField={setField} save={() => saveKeys(["gsc_site_url"])} configured={status?.fields?.gsc_site_url?.configured} />
        <AnalyticsCard ready={sys.analytics?.ready} form={form} setField={setField} save={() => saveKeys(["ga4_property_id"])} />
        <PageSpeedCard ready={sys.pagespeed?.ready} form={form} setField={setField} save={() => saveKeys(["google_pagespeed_api_key"])} configured={status?.fields?.google_pagespeed_api_key?.configured} />
        <BusinessProfileCard ready={sys.business_profile?.ready} form={form} setField={setField} save={() => saveKeys(["gbp_account_id"])} />
        <BingCard ready={sys.bing?.ready} form={form} setField={setField} save={() => saveKeys(["bing_api_key", "bing_site_url"])} configured={status?.fields?.bing_api_key?.configured} />
        <SchemaCard />
      </div>
    </AppShell>
  );
}

function RangeRow({ start, end, setStart, setEnd, onRun, busy, testid }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div><Label>Start date</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="font-mono text-sm w-40" /></div>
      <div><Label>End date</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="font-mono text-sm w-40" /></div>
      <Button size="sm" onClick={onRun} disabled={busy} data-testid={testid}>{busy ? "Loading\u2026" : <><Refresh width={14} height={14} className="mr-1" /> Fetch</>}</Button>
    </div>
  );
}

function SearchConsoleCard({ ready, form, setField, save, configured }) {
  const [start, setStart] = useState(isoDaysAgo(28));
  const [end, setEnd] = useState(isoDaysAgo(2));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setData(await integrations.searchConsole(start, end)); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Search} title="Google Search Console" desc="Top queries with clicks, impressions, CTR and average position." ready={ready}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]"><KeyField label="Verified site URL" value={form.gsc_site_url} onChange={setField("gsc_site_url")} placeholder="https://www.example.com/ or sc-domain:example.com" testid="input-gsc-site" /></div>
        <Button variant="outline" size="sm" onClick={save} data-testid="save-gsc">Save</Button>
      </div>
      <Separator />
      <RangeRow start={start} end={end} setStart={setStart} setEnd={setEnd} onRun={run} busy={busy} testid="fetch-gsc" />
      {data && (
        <>
          <div className="flex gap-6 font-mono text-xs"><span>Clicks: <b>{data.totals?.clicks}</b></span><span>Impressions: <b>{data.totals?.impressions}</b></span></div>
          <DataTable columns={[{ key: "key", label: "Query" }, { key: "clicks", label: "Clicks" }, { key: "impressions", label: "Impr." }, { key: "ctr", label: "CTR %" }, { key: "position", label: "Pos." }]} rows={data.rows} />
        </>
      )}
    </Section>
  );
}

function AnalyticsCard({ ready, form, setField, save }) {
  const [start, setStart] = useState(isoDaysAgo(28));
  const [end, setEnd] = useState(isoDaysAgo(1));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setData(await integrations.analytics(start, end)); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Activity} title="Google Analytics (GA4)" desc="Sessions, users and pageviews by day from the GA4 Data API." ready={ready}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]"><KeyField label="GA4 Property ID" value={form.ga4_property_id} onChange={setField("ga4_property_id")} placeholder="e.g. 123456789" testid="input-ga4" /></div>
        <Button variant="outline" size="sm" onClick={save} data-testid="save-ga4">Save</Button>
      </div>
      <Separator />
      <RangeRow start={start} end={end} setStart={setStart} setEnd={setEnd} onRun={run} busy={busy} testid="fetch-ga4" />
      {data && (
        <>
          <div className="flex gap-6 font-mono text-xs"><span>Sessions: <b>{data.totals?.sessions}</b></span><span>Users: <b>{data.totals?.users}</b></span><span>Pageviews: <b>{data.totals?.pageviews}</b></span></div>
          <DataTable columns={[{ key: "date", label: "Date" }, { key: "sessions", label: "Sessions" }, { key: "users", label: "Users" }, { key: "pageviews", label: "Pageviews" }]} rows={data.rows} />
        </>
      )}
    </Section>
  );
}

function PageSpeedCard({ ready, form, setField, save, configured }) {
  const [url, setUrl] = useState("");
  const [strategy, setStrategy] = useState("mobile");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!url) { toast.error("Enter a URL"); return; }
    setBusy(true);
    try { setData(await integrations.pagespeed(url, strategy)); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  const scoreColor = (v) => (v == null ? "text-muted-foreground" : v >= 90 ? "text-emerald-600" : v >= 50 ? "text-amber-600" : "text-red-600");
  return (
    <Section icon={Activity} title="PageSpeed Insights" desc="Lighthouse scores + Core Web Vitals for any page." ready={ready}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]"><KeyField label="Google API Key" value={form.google_pagespeed_api_key} onChange={setField("google_pagespeed_api_key")} placeholder={configured ? "\u2022\u2022\u2022\u2022 saved \u2014 re-enter to change" : "AIza..."} type="password" testid="input-pagespeed-key" /></div>
        <Button variant="outline" size="sm" onClick={save} data-testid="save-pagespeed">Save</Button>
      </div>
      <Separator />
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]"><Label>URL to analyze</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" className="font-mono text-sm" data-testid="input-pagespeed-url" /></div>
        <div><Label>Strategy</Label>
          <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className="h-10 border border-border bg-background px-3 text-sm font-mono" data-testid="select-pagespeed-strategy"><option value="mobile">Mobile</option><option value="desktop">Desktop</option></select>
        </div>
        <Button size="sm" onClick={run} disabled={busy} data-testid="fetch-pagespeed">{busy ? "Running\u2026" : "Run"}</Button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Performance", data.scores?.performance], ["SEO", data.scores?.seo], ["Accessibility", data.scores?.accessibility], ["Best practices", data.scores?.best_practices]].map(([l, v]) => (
              <div key={l} className="border border-border p-3 text-center"><div className={`font-display text-2xl ${scoreColor(v)}`}>{v ?? "\u2014"}</div><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{l}</div></div>
            ))}
          </div>
          <DataTable columns={[{ key: "metric", label: "Metric" }, { key: "value", label: "Value" }]} rows={Object.entries(data.metrics || {}).map(([k, v]) => ({ metric: k.replace(/_/g, " "), value: v || "\u2014" }))} />
        </>
      )}
    </Section>
  );
}

function BusinessProfileCard({ ready, form, setField, save }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setData(await integrations.businessProfile()); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={MapPin} title="Google Business Profile" desc="Business locations linked to the connected account." ready={ready}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]"><KeyField label="Business Account ID" value={form.gbp_account_id} onChange={setField("gbp_account_id")} placeholder="accounts/1234567890 or 1234567890" testid="input-gbp" /></div>
        <Button variant="outline" size="sm" onClick={save} data-testid="save-gbp">Save</Button>
        <Button size="sm" onClick={run} disabled={busy} data-testid="fetch-gbp">{busy ? "Loading\u2026" : "Fetch locations"}</Button>
      </div>
      {data && (
        <DataTable columns={[{ key: "title", label: "Name" }, { key: "address", label: "Address" }, { key: "website", label: "Website" }]} rows={data.locations} />
      )}
    </Section>
  );
}

function BingCard({ ready, form, setField, save, configured }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setData(await integrations.bing()); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Globe} title="Bing Webmaster Tools" desc="Rank, traffic and crawl statistics from Bing." ready={ready}>
      <div className="grid sm:grid-cols-2 gap-4">
        <KeyField label="Bing API Key" value={form.bing_api_key} onChange={setField("bing_api_key")} placeholder={configured ? "\u2022\u2022\u2022\u2022 saved \u2014 re-enter to change" : "API key"} type="password" testid="input-bing-key" />
        <KeyField label="Verified site URL" value={form.bing_site_url} onChange={setField("bing_site_url")} placeholder="https://www.example.com" testid="input-bing-site" />
      </div>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={save} data-testid="save-bing">Save</Button>
        <Button size="sm" onClick={run} disabled={busy} data-testid="fetch-bing">{busy ? "Loading\u2026" : "Fetch stats"}</Button>
      </div>
      {data && (
        <pre className="border border-border bg-muted/40 p-3 text-xs overflow-x-auto max-h-72">{JSON.stringify(data, null, 2)}</pre>
      )}
    </Section>
  );
}

function SchemaCard() {
  const [url, setUrl] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!url) { toast.error("Enter a URL"); return; }
    setBusy(true);
    try { setData(await integrations.schemaValidate(url)); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Code} title="Schema.org Validator" desc="Detect and validate structured data (JSON-LD / microdata). No credentials required." ready={true}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]"><Label>URL to validate</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" className="font-mono text-sm" data-testid="input-schema-url" /></div>
        <Button size="sm" onClick={run} disabled={busy} data-testid="fetch-schema">{busy ? "Validating\u2026" : "Validate"}</Button>
      </div>
      {data && (
        <>
          <div className="font-mono text-xs text-muted-foreground">Source: {data.source} \u00b7 Items found: <b className="text-foreground">{data.count}</b>{data.errors?.length ? ` \u00b7 ${data.errors.length} error(s)` : ""}</div>
          <DataTable columns={[{ key: "type", label: "Type" }, { key: "format", label: "Format" }, { key: "num_properties", label: "Properties" }]} rows={(data.items || []).map((it) => ({ type: Array.isArray(it.type) ? it.type.join(", ") : (it.type || "\u2014"), format: it.format || "\u2014", num_properties: it.num_properties ?? "\u2014" }))} />
          {data.errors?.length > 0 && <div className="text-xs text-red-600 font-mono">{data.errors.join("; ")}</div>}
        </>
      )}
    </Section>
  );
}
